/**
 * Background themes for tenant white-labeling.
 *
 * Every theme is pure CSS (no image assets) so:
 *   - Zero network cost / instant paint
 *   - Automatically tints with the tenant's --primary / --accent CSS vars,
 *     so a Rose-palette tenant with the "Aurora" theme gets pink aurora
 *     while a Forest-palette tenant with the same theme gets green aurora.
 *
 * The value stored on `tenants.background_theme` is one of the keys below.
 * `TenantBrandingBridge` stamps that key onto <body data-bg-theme="..."> and
 * the matching class in `index.css` renders the effect.
 */
export const BACKGROUND_THEMES = [
  {
    id: "none",
    label: "Clean",
    desc: "Solid, distraction-free",
    swatch: "linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--muted)) 100%)",
  },
  {
    id: "aurora",
    label: "Aurora",
    desc: "Soft glowing blobs, tenant-tinted",
    swatch: "radial-gradient(circle at 20% 20%, hsl(var(--primary) / 0.55), transparent 55%), radial-gradient(circle at 80% 70%, hsl(var(--accent) / 0.45), transparent 55%), hsl(var(--background))",
  },
  {
    id: "mesh",
    label: "Mesh gradient",
    desc: "Multi-point gradient wash",
    swatch: "radial-gradient(at 15% 15%, hsl(var(--primary) / 0.5), transparent 40%), radial-gradient(at 85% 20%, hsl(var(--accent) / 0.45), transparent 45%), radial-gradient(at 50% 100%, hsl(var(--primary) / 0.4), transparent 55%), hsl(var(--background))",
  },
  {
    id: "grid",
    label: "Grid",
    desc: "Fine tenant-tinted grid lines",
    swatch: "linear-gradient(hsl(var(--primary) / 0.16) 1px, transparent 1px) 0 0 / 22px 22px, linear-gradient(90deg, hsl(var(--primary) / 0.16) 1px, transparent 1px) 0 0 / 22px 22px, hsl(var(--background))",
  },
  {
    id: "dots",
    label: "Dots",
    desc: "Subtle dot lattice",
    swatch: "radial-gradient(hsl(var(--primary) / 0.35) 1.2px, transparent 1.2px) 0 0 / 18px 18px, hsl(var(--background))",
  },
  {
    id: "waves",
    label: "Waves",
    desc: "Flowing diagonal waves",
    swatch: "repeating-linear-gradient(-45deg, hsl(var(--primary) / 0.06) 0 12px, transparent 12px 24px), hsl(var(--background))",
  },
  {
    id: "topography",
    label: "Topography",
    desc: "Contour-map inspired",
    swatch: "repeating-radial-gradient(circle at 30% 30%, hsl(var(--primary) / 0.12) 0 2px, transparent 2px 20px), hsl(var(--background))",
  },
  {
    id: "noise",
    label: "Grain",
    desc: "Film-grain texture",
    swatch: "hsl(var(--background))",
  },
  {
    id: "sunset",
    label: "Sunset glow",
    desc: "Warm gradient horizon",
    swatch: "linear-gradient(180deg, hsl(var(--background)) 0%, hsl(var(--primary) / 0.28) 60%, hsl(var(--accent) / 0.22) 100%)",
  },
  {
    id: "custom",
    label: "Custom",
    desc: "Your uploaded wallpaper",
    swatch: "hsl(var(--muted))",
    isCustom: true,
  },
];

/** Apply theme by stamping the id onto document.body so CSS in index.css takes over.
 *  When `wallpaperUrl` is provided, also stamps it into a CSS variable so the
 *  `custom` theme's `background-image: var(--tenant-wallpaper)` picks it up.
 *  `overlayPct` (0..90) controls the legibility dim over the custom wallpaper. */
export function applyBackgroundTheme(themeId, wallpaperUrl, overlayPct) {
  if (typeof document === "undefined") return;
  const id = (themeId && String(themeId)) || "none";
  document.body.dataset.bgTheme = id;
  if (wallpaperUrl) {
    document.documentElement.style.setProperty("--tenant-wallpaper", `url("${wallpaperUrl}")`);
  } else if (id !== "custom") {
    document.documentElement.style.removeProperty("--tenant-wallpaper");
  }
  const n = Number.isFinite(+overlayPct) ? +overlayPct : null;
  if (n !== null) {
    // Clamp 0..90 → 0..0.9 for CSS alpha
    const alpha = Math.max(0, Math.min(90, n)) / 100;
    document.documentElement.style.setProperty("--tenant-wallpaper-overlay", alpha.toFixed(2));
  }
}
